/**
 * Multi-piece product customization — couple sets, trios, etc.
 * Cart/order stores compact JSON prefixed with `m:` (see serializePieceCustomization).
 */

import {
  isAllowedCharLetter,
  normalizePersonalizationLetter,
  productCharPersonalizationEnabled,
} from '../productPersonalization';
import {
  formatJewelryCustomizationLabel,
  getJewelryProfile,
  getProductJewelryProfileId,
  jewelryCustomizationPriceDelta,
  normalizeProductJewelryOptions,
  productJewelryCustomizationEnabled,
  validateJewelryCustomization,
} from '../jewelry/jewelryCustomization';
import {
  getProductPieceConfig,
  normalizePieceConfig,
  productMultiPieceEnabled,
} from './pieceConfig';

export {
  defaultPieceLabels,
  getProductPieceConfig,
  MAX_PIECE_COUNT,
  MIN_PIECE_COUNT,
  normalizePieceConfig,
  productMultiPieceEnabled,
} from './pieceConfig';

export const PIECE_PREFIX = 'm:';
/** @param {object} product */
export function emptyPieceLetterValues(product) {
  return Array(getProductPieceConfig(product).count).fill('');
}

/** @param {object} product */
export function emptyPieceJewelryValues(product) {
  return Array.from({ length: getProductPieceConfig(product).count }, () => ({}));
}

/** @param {object} product */
export function pieceCustomizationMode(product) {
  if (productJewelryCustomizationEnabled(product)) return 'jewelry';
  if (productCharPersonalizationEnabled(product)) return 'letter';
  return '';
}

/** @param {object} product */
export function productNeedsPieceCustomization(product) {
  return productMultiPieceEnabled(product) && Boolean(pieceCustomizationMode(product));
}

/** @param {object} product */
export function buildProductCustomizationOptionsForSave(product) {
  const profile = String(product?.customization_profile || '').trim();
  const rawOpts =
    product?.customization_options && typeof product.customization_options === 'object'
      ? { ...product.customization_options }
      : {};
  const pieceConfig = normalizePieceConfig(rawOpts.pieceConfig);
  if (pieceConfig.count > 1) {
    rawOpts.pieceConfig = pieceConfig;
  } else {
    delete rawOpts.pieceConfig;
  }
  if (profile) {
    return normalizeProductJewelryOptions({
      customization_profile: profile,
      customization_options: rawOpts,
    });
  }
  return Object.keys(rawOpts).length ? rawOpts : {};
}

/** @param {object} product */
export function normalizeProductCustomizationOptions(product) {
  const profileId = getProductJewelryProfileId(product);
  let opts = {};
  const raw = product?.customization_options;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    opts = { ...raw };
  } else if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        opts = { ...parsed };
      }
    } catch {
      opts = {};
    }
  }
  if (profileId) {
    opts = normalizeProductJewelryOptions({
      customization_profile: profileId,
      customization_options: opts,
    });
  }
  const pieceConfig = normalizePieceConfig(opts.pieceConfig);
  if (pieceConfig.count > 1) {
    opts.pieceConfig = pieceConfig;
  } else {
    delete opts.pieceConfig;
  }
  return opts;
}

/** @param {string[]} letterValues @param {object} product */
export function validatePieceLetterCustomization(letterValues, product) {
  const { count, labels } = getProductPieceConfig(product);
  const errors = {};
  for (let i = 0; i < count; i += 1) {
    const letter = normalizePersonalizationLetter(letterValues?.[i]);
    if (!letter || !isAllowedCharLetter(letter, product)) {
      errors[i] = `Choose a letter for ${labels[i]}.`;
    }
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/** @param {object[]} jewelryValues @param {object} product */
export function validatePieceJewelryCustomization(jewelryValues, product) {
  const { count, labels } = getProductPieceConfig(product);
  const errors = {};
  for (let i = 0; i < count; i += 1) {
    const check = validateJewelryCustomization(jewelryValues?.[i] || {}, product);
    if (!check.ok) {
      errors[i] = `Complete options for ${labels[i]}.`;
    }
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/** @param {string[]|object[]} values @param {object} product */
export function validatePieceCustomization(values, product) {
  const mode = pieceCustomizationMode(product);
  if (mode === 'letter') return validatePieceLetterCustomization(values, product);
  if (mode === 'jewelry') return validatePieceJewelryCustomization(values, product);
  return { ok: true, errors: {} };
}

/** @param {string[]} letterValues @param {object} product */
export function formatPieceLetterLabel(letterValues, product) {
  const { count, labels } = getProductPieceConfig(product);
  const parts = [];
  for (let i = 0; i < count; i += 1) {
    const letter = normalizePersonalizationLetter(letterValues?.[i]);
    if (letter) parts.push(`${labels[i]}: ${letter}`);
  }
  return parts.join(' · ');
}

/** @param {object[]} jewelryValues @param {object} product */
export function formatPieceJewelryLabel(jewelryValues, product) {
  const { count, labels } = getProductPieceConfig(product);
  const parts = [];
  for (let i = 0; i < count; i += 1) {
    const detail = formatJewelryCustomizationLabel(jewelryValues?.[i] || {}, product);
    if (detail) parts.push(`${labels[i]}: ${detail}`);
  }
  return parts.join(' · ');
}

/** @param {string[]|object[]} values @param {object} product */
export function formatPieceCustomizationLabel(values, product) {
  const mode = pieceCustomizationMode(product);
  if (mode === 'letter') return formatPieceLetterLabel(values, product);
  if (mode === 'jewelry') return formatPieceJewelryLabel(values, product);
  return '';
}

/** @param {string[]|object[]} values @param {object} product */
export function pieceCustomizationPriceDelta(values, product) {
  const mode = pieceCustomizationMode(product);
  if (mode !== 'jewelry') return 0;
  let delta = 0;
  for (const pieceValues of values || []) {
    delta += jewelryCustomizationPriceDelta(pieceValues || {}, product);
  }
  return delta;
}

/** @param {string[]|object[]} values @param {object} product */
export function serializePieceCustomization(values, product) {
  const mode = pieceCustomizationMode(product);
  const { count, labels } = getProductPieceConfig(product);
  const pieces = [];
  if (mode === 'letter') {
    for (let i = 0; i < count; i += 1) {
      pieces.push({
        label: labels[i],
        letter: normalizePersonalizationLetter(values?.[i]),
      });
    }
  } else if (mode === 'jewelry') {
    for (let i = 0; i < count; i += 1) {
      pieces.push({
        label: labels[i],
        values: { ...(values?.[i] || {}) },
      });
    }
  }
  const payload = {
    v: 1,
    mode,
    profile: getProductJewelryProfileId(product) || undefined,
    pieces,
    label: formatPieceCustomizationLabel(values, product),
  };
  return PIECE_PREFIX + JSON.stringify(payload);
}

/** @param {string} raw */
export function parsePieceCustomization(raw) {
  const s = String(raw || '').trim();
  if (!s.startsWith(PIECE_PREFIX)) return null;
  try {
    const data = JSON.parse(s.slice(PIECE_PREFIX.length));
    if (!data || typeof data !== 'object') return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Structured customization for admin orders and detailed displays.
 * @param {string} raw
 * @param {object} [product]
 */
export function describePieceCustomization(raw, product = null) {
  const parsed = parsePieceCustomization(raw);
  if (!parsed) return null;

  const mode = String(parsed.mode || '');
  const piecesRaw = Array.isArray(parsed.pieces) ? parsed.pieces : [];
  const pieces = piecesRaw.map((piece, index) => {
    const label = String(piece?.label || `Piece ${index + 1}`).trim();
    const fields = [];
    if (mode === 'letter') {
      const letter = normalizePersonalizationLetter(piece?.letter);
      if (letter) {
        fields.push({ id: 'letter', label: 'Letter', type: 'text', value: letter });
      }
    } else if (mode === 'jewelry') {
      const values = piece?.values && typeof piece.values === 'object' ? piece.values : {};
      const productLike =
        product ||
        (parsed.profile ? { customization_profile: parsed.profile } : null);
      if (productLike) {
        const detail = formatJewelryCustomizationLabel(values, productLike);
        if (detail) {
          fields.push({ id: 'summary', label: 'Options', type: 'text', value: detail });
        }
      }
    }
    return { label, fields };
  });

  let profileLabel = 'Multi-piece set';
  if (mode === 'letter') profileLabel = 'Monogram set';
  if (mode === 'jewelry') {
    const profile = getJewelryProfile(parsed.profile || getProductJewelryProfileId(product || {}));
    profileLabel = profile?.label ? `${profile.label} (set)` : 'Custom set';
  }

  return {
    mode,
    profileLabel,
    summary: parsed.label || pieces.map((p) => p.label).join(' · '),
    pieces,
  };
}

/** @param {string} raw */
export function formatOrderItemPieceCustomizationPlain(raw) {
  const described = describePieceCustomization(raw);
  if (!described) return '';
  const lines = [];
  for (const piece of described.pieces) {
    const detail = piece.fields.map((f) => `${f.label}: ${f.value}`).join(' · ');
    if (detail) lines.push(`${piece.label}: ${detail}`);
    else if (piece.label) lines.push(piece.label);
  }
  return lines.join(' · ') || described.summary || 'Multi-piece set';
}
